import produce from 'immer';
import {
  type CategoryData,
  FeatureType,
  type GeneralID,
  type SceneData,
  type TourData,
} from '@types';
import type { sceneCopy } from '@apis/tourApis';
import { $categoryList, $categoryMap, $sceneMap } from '@stores/dataStores';
import type { ScenesCopyProps, ScenesCopyState } from './scenesCopyContext';

type Result = Pick<Parameters<typeof sceneCopy>[0], 'category' | 'scenes'>;

export function getSceneCopyParams(
  props: ScenesCopyProps,
  target: Required<
    Pick<
      ScenesCopyState,
      'targetCategoryId' | 'isNewCategory' | 'targetTourData'
    >
  >
): Result {
  const { level, categoryId, subCategoryId, sceneIds } = props;
  const { targetTourData, targetCategoryId, isNewCategory } = target;

  if (level === '作品') {
    return appendCategory({ targetTourData });
  }
  if (level === '一级分组' && categoryId !== undefined) {
    return appendCategory({ targetTourData, categoryId });
  }
  if (level === '二级分组' && subCategoryId !== undefined) {
    return copyToCategory({
      targetTourData,
      targetCategoryId,
      subCategoryId,
      isNewCategory,
    });
  }
  if (level === '场景' && sceneIds !== undefined) {
    return copyScenes({
      sceneIds,
      targetTourData,
      targetCategoryId,
      isNewCategory,
    });
  }

  throw new Error('getSceneCopyParams: 参数错误');
}

function appendCategory(params: {
  /** 所选的一级分组, 不传表示整个作品 */
  categoryId?: number;
  targetTourData: TourData;
}): Result {
  const { targetTourData, categoryId } = params;
  const cList =
    categoryId === undefined ? $categoryList.getState() : [categoryId];
  const cMap = $categoryMap.getState();
  const sMap = $sceneMap.getState();
  const category: Result['category'] = [];
  let scenes: Result['scenes'] = [];
  let largestId = getLargestCategoryId(targetTourData.category);
  for (let i = 0; i < cList.length; i++) {
    const cItem = cMap.get(cList[i])!;
    largestId += 1;
    const cId = largestId;
    if (cItem.categoryIds.length) {
      const children: CategoryData[] = [];
      for (let j = 0; j < cItem.categoryIds.length; j++) {
        const subItem = cMap.get(cItem.categoryIds[j])!;
        largestId += 1;
        const subId = largestId;
        let sort = 0;
        for (let k = 0; k < subItem.sceneIds.length; k++) {
          const sceneItem = sMap.get(subItem.sceneIds[k]);
          if (sceneItem) {
            sort += 1;
            scenes.push({
              ...sceneItem,
              categoryId: subId,
              sort,
              tid: targetTourData.tid,
            });
          }
        }
        children.push({ id: subId, name: subItem.name });
      }
      category.push({ id: cId, name: cItem.name, children });
    } else {
      let sort = 0;
      for (let m = 0; m < cItem.sceneIds.length; m++) {
        const sceneItem = sMap.get(cItem.sceneIds[m]);
        if (sceneItem) {
          sort += 1;
          scenes.push({
            ...sceneItem,
            categoryId: cId,
            sort,
            tid: targetTourData.tid,
          });
        }
      }
      category.push({ id: cId, name: cItem.name });
    }
  }

  scenes = filterSceneHotspots(scenes);

  return {
    category: [...targetTourData.category, ...category],
    scenes,
  };
}

function copyToCategory(params: {
  /** 选中的二级分组 */
  subCategoryId: number;
  targetTourData: TourData;
  targetCategoryId: number;
  isNewCategory: boolean;
}): Result {
  const { subCategoryId, targetTourData, targetCategoryId, isNewCategory } =
    params;
  const cMap = $categoryMap.getState();
  const sMap = $sceneMap.getState();
  let scenes: Result['scenes'] = [];
  let sort = 0;

  const cItem = cMap.get(subCategoryId)!;
  const cId = getLargestCategoryId(targetTourData.category) + 1;

  let category: Result['category'] = [];
  if (isNewCategory) {
    // 作为新的一级分组
    category = produce(targetTourData.category, (draft) => {
      draft.push({ id: cId, name: cItem.name });
    });
  } else {
    // 添加到已有的一级分组
    category = produce(targetTourData.category, (draft) => {
      const item = draft.find((x) => x.id === targetCategoryId);
      if (!item) return;
      item.children?.push({ id: cId, name: cItem.name });
    });
  }

  for (let i = 0; i < cItem.sceneIds.length; i++) {
    const sceneItem = sMap.get(cItem.sceneIds[i]);
    if (sceneItem) {
      sort += 1;
      scenes.push({
        ...sceneItem,
        categoryId: cId,
        sort,
        tid: targetTourData.tid,
      });
    }
  }

  scenes = filterSceneHotspots(scenes);

  return { category, scenes };
}

function copyScenes(params: {
  /** 选中的场景 */
  sceneIds: GeneralID[];
  targetTourData: TourData;
  targetCategoryId: number;
  isNewCategory: boolean;
}): Result {
  const { sceneIds, targetTourData, targetCategoryId, isNewCategory } = params;
  const sMap = $sceneMap.getState();
  let scenes: Result['scenes'] = [];
  let sort = 0;

  // 如果是新分组则使用新id, 否则使用目标分组id
  const cId = isNewCategory
    ? getLargestCategoryId(targetTourData.category) + 1
    : targetCategoryId;
  const newItem = { id: cId, name: '跨作品复制分组' };

  let category: Result['category'] = [];
  if (isNewCategory) {
    // 作为新的一级分组或二级分组
    if (targetCategoryId === 0) {
      // 作为新的一级分组
      category = produce(targetTourData.category, (draft) => {
        draft.push(newItem);
      });
    } else {
      // 作为新的二级分组添加到已有的一级分组
      category = produce(targetTourData.category, (draft) => {
        const item = getCategoryItemById(draft, targetCategoryId);
        if (!item) return;
        // 目标分组下的场景
        item.children?.push(newItem);
      });
    }
  } else {
    category = targetTourData.category;
    const targetScenes = targetTourData.scenes.filter(
      (x) => x.categoryId === targetCategoryId
    );
    sort = getLargestSort(targetScenes);
  }

  for (let m = 0; m < sceneIds.length; m++) {
    const sceneItem = sMap.get(sceneIds[m]);
    if (sceneItem) {
      sort += 1;
      scenes.push({
        ...sceneItem,
        categoryId: cId,
        sort,
        tid: targetTourData.tid,
      });
    }
  }

  scenes = filterSceneHotspots(scenes);

  return { category, scenes };
}

function getLargestCategoryId(category: CategoryData[] = []) {
  let largestCategoryId = 0;
  const fn = (_category: CategoryData[]) => {
    _category.forEach((item) => {
      if (item.id > largestCategoryId) {
        largestCategoryId = item.id;
      }
      if (item.children?.length) {
        fn(item.children);
      }
    });
  };
  fn(category);
  return largestCategoryId;
}

function getCategoryItemById(
  category: CategoryData[],
  id: number
): CategoryData | undefined {
  const fn = (_category: CategoryData[]): CategoryData | undefined => {
    for (let i = 0; i < _category.length; i++) {
      if (_category[i].id === id) {
        return _category[i];
      }
      const list = _category[i].children;
      if (list) {
        const result = fn(list);
        if (result) return result;
      }
    }
  };
  return fn(category);
}

function getLargestSort(scenes: SceneData[]) {
  let largestSort = 0;
  scenes.forEach((item) => {
    if (item.sort > largestSort) {
      largestSort = item.sort;
    }
  });
  return largestSort;
}

/**
 * 过滤场景切换热点
 * 如果场景切换热点的目标场景不在复制的场景中, 则去除这个热点
 */
function filterSceneHotspots(scenes: SceneData[]) {
  const ids = scenes.map((x) => x.id);
  scenes.forEach((sceneItem) => {
    if (sceneItem.hotspot?.length) {
      sceneItem.hotspot = sceneItem.hotspot.filter((hotspot) => {
        if (hotspot.data.code !== FeatureType.scene) return true;
        if (ids.includes(hotspot.data.sceneId)) return true;
        return false;
      });
    }
  });
  return scenes;
}
